// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("360wallet_token"));
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem("360wallet_token");
    setToken(null);
    setUser(null);
    setRequires2FA(false);
    setPendingCredentials(null);
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error("Token verification failed:", error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, logout]);

  const login = async (email, password, totpCode = null) => {
    const payload = { email, password };
    if (totpCode) {
      payload.totp_code = totpCode;
    }
    
    const response = await axios.post(`${API}/auth/login`, payload);
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      setRequires2FA(true);
      setPendingCredentials({ email, password });
      return { requires_2fa: true };
    }
    
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem("360wallet_token", newToken);
    setToken(newToken);
    setUser(userData);
    setRequires2FA(false);
    setPendingCredentials(null);
    
    return userData;
  };

  const verify2FA = async (totpCode) => {
    if (!pendingCredentials) {
      throw new Error("No pending login");
    }
    return login(pendingCredentials.email, pendingCredentials.password, totpCode);
  };

  const cancel2FA = () => {
    setRequires2FA(false);
    setPendingCredentials(null);
  };

  const register = async (email, password, fullName, role = "operator") => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName,
      role
    });
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    requires2FA,
    login,
    logout,
    register,
    verify2FA,
    cancel2FA
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
